import React, { useState, useEffect, useRef } from 'react';
import { Cell, Grid } from 'react-md';
import { Checkbox } from '@mui/material';
import ApiRequestService from '../../../../../components/ApiRequestService';

const EXPECT_ALL_KEY = '__expect_all__';

const parseSelectedAnswers = (selectedAnswers) => {
    if (!selectedAnswers) return [];
    if (Array.isArray(selectedAnswers)) return selectedAnswers;
    if (typeof selectedAnswers === 'object' && Array.isArray(selectedAnswers.excluded_ao)) return selectedAnswers.excluded_ao;
    if (typeof selectedAnswers === 'string') {
        try {
            const parsed = JSON.parse(selectedAnswers);
            if (Array.isArray(parsed)) return parsed;
            if (parsed && Array.isArray(parsed.excluded_ao)) return parsed.excluded_ao;
        } catch { /* ignore */ }
    }
    return [];
};

const SFExcludeAOCheckboxAnswers = ({ selectedSubcategoryKeys = [], onAnswerEntered, selectedAnswers }) => {
    const [subcategoryNames, setSubcategoryNames] = useState([]);
    const [localValueArr, setLocalValueArr] = useState(() => parseSelectedAnswers(selectedAnswers));
    const isInternalUpdate = useRef(false);

    const keysStr = [...selectedSubcategoryKeys].sort().join(',');

    useEffect(() => {
        if (isInternalUpdate.current) {
            isInternalUpdate.current = false;
            return;
        }
        setLocalValueArr(parseSelectedAnswers(selectedAnswers));
    }, [selectedAnswers]);

    useEffect(() => {
        if (!keysStr) { setSubcategoryNames([]); return; }

        const parsed = keysStr.split(',').map(key => {
            const [subject_id, topic_id, category_id, subcategory_id] = key.split('_').map(Number);
            return { key, subject_id, topic_id, category_id, subcategory_id };
        });

        const byCategory = {};
        parsed.forEach(p => {
            const ck = `${p.subject_id}_${p.topic_id}_${p.category_id}`;
            if (!byCategory[ck]) byCategory[ck] = { ...p, items: [] };
            byCategory[ck].items.push(p);
        });

        Promise.all(
            Object.values(byCategory).map(async (group) => {
                const subRes = await ApiRequestService({
                    method: 'post', url: '/getAllSubCategories',
                    data: { subject_id: group.subject_id, topic_id: group.topic_id, category_id: group.category_id },
                });

                const subcategories = subRes?.data?.data?.subCategory_names || [];
                const selectedIds = new Set(group.items.map(i => String(i.subcategory_id)));

                return subcategories
                    .filter(sc => selectedIds.has(String(sc.subcategory_id)))
                    .map(sc => ({
                        key: group.items.find(i => String(i.subcategory_id) === String(sc.subcategory_id)).key,
                        name: sc.subcategory_name,
                    }));
            })
        ).then(results => setSubcategoryNames(results.flat()));
    }, [keysStr]);

    const removeElementFromArray = (arr, element) => {
        var i = arr.indexOf(element);
        if (i !== -1) {
            arr.splice(i, 1);
        }
        return arr;
    };

    const handleClick = (key) => {
        let newLocalValueArr;
        if (key === EXPECT_ALL_KEY) {
            if (localValueArr.includes(EXPECT_ALL_KEY)) {
                newLocalValueArr = [];
            } else {
                newLocalValueArr = [EXPECT_ALL_KEY];
            }
        } else {
            newLocalValueArr = [...localValueArr].filter(k => k !== EXPECT_ALL_KEY);
            if (newLocalValueArr.includes(key)) {
                newLocalValueArr = removeElementFromArray(newLocalValueArr, key);
            } else {
                newLocalValueArr.push(key);
            }
        }
        setLocalValueArr(newLocalValueArr);
        if (onAnswerEntered) {
            isInternalUpdate.current = true;
            onAnswerEntered(JSON.stringify({ excluded_ao: newLocalValueArr }));
        }
    };

    if (subcategoryNames.length === 0) return null;

    return (
        <div>
            <div className='mb-2 text-secondary'>Select one or more from the below options:</div>
            <Grid>
                {subcategoryNames.map((sc, index) => (
                    <Cell
                        key={sc.key}
                        className="w-full h-full cursor-pointer"
                        size={12}
                        onClick={(e) => {
                            e.preventDefault();
                            handleClick(sc.key);
                        }}
                    >
                        <div className={`sf-ans-btn d-flex justify-content-center align-items-center cursor-pointer ${localValueArr.includes(sc.key) ? 'sf-ans-btn-selected' : ''}`}>
                            <Checkbox
                                size='small'
                                className='p-0 mr-2'
                                id={'exclude-ao-checkbox-' + index}
                                type='checkbox'
                                value={sc.key}
                                checked={localValueArr.includes(sc.key)}
                                sx={{ color: '#bb0000', '&.Mui-checked': { color: '#bb0000' } }}
                            />
                            <label className='cursor-pointer p-0 m-0 w-100' htmlFor={'exclude-ao-checkbox-' + index}>
                                {sc.name}
                            </label>
                        </div>
                    </Cell>
                ))}
                <Cell
                    key={EXPECT_ALL_KEY}
                    className="w-full h-full cursor-pointer"
                    size={12}
                    onClick={(e) => {
                        e.preventDefault();
                        handleClick(EXPECT_ALL_KEY);
                    }}
                >
                    <div className={`sf-ans-btn d-flex justify-content-center align-items-center cursor-pointer ${localValueArr.includes(EXPECT_ALL_KEY) ? 'sf-ans-btn-selected' : ''}`}>
                        <Checkbox
                            size='small'
                            className='p-0 mr-2'
                            id={'exclude-ao-checkbox-expect-all'}
                            type='checkbox'
                            value={EXPECT_ALL_KEY}
                            checked={localValueArr.includes(EXPECT_ALL_KEY)}
                            sx={{ color: '#bb0000', '&.Mui-checked': { color: '#bb0000' } }}
                        />
                        <label className='cursor-pointer p-0 m-0 w-100' htmlFor={'exclude-ao-checkbox-expect-all'}>
                            I expect my students to be familiar with all these contexts
                        </label>
                    </div>
                </Cell>
            </Grid>
        </div>
    );
};

export default SFExcludeAOCheckboxAnswers;
